import { Box, Checkbox, FormHelperText, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectBasketId, selectExitStrategy, selectStrategyBuilderMode, selectTradeOrders } from '../../../selectors/strategyBuilder'
import { Broker_OrderType, OrderStatus, OrderType } from '../../../proto/generated/order'
import { updateExitStrategy } from '../../../reducer/strategyBuilder.reducer'
import { selectOrdersHistory } from '../../../selectors/orders'
import { useStyles } from './styles'
import { ExtraSmallButton } from '../../custom'
import ClearIcon from '../../../assets/icons/ClearIcon'
import { ModifyType } from '../../ordersWidget/OrderRow'
import { AnalyzeGroupedPosition, GroupedPosition } from '../../../reducer/positions.reducer'
import { selectSnapshots } from '../../../selectors/security'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'

export type SlTargetType = 'points' | 'percentage' | 'exact'

export const orderTypes = [
    {
        value: Broker_OrderType.LIMIT_ORDER,
        label: 'Limit Order'
    },
    {
        value: Broker_OrderType.MARKET_ORDER,
        label: 'Market Order'
    }
]

const SlAndTargetInputs: React.FC<{
    disabled: boolean, disabledCheckbox?: boolean;
    disableStopLoss?: boolean;
    disableTarget?: boolean;
    initialStopLoss?: number
    initialTarget?: number
    modifyType?: ModifyType
    basketPosition?: GroupedPosition
    strategyMode?: 'trade' | 'analyze' | 'squareOff'
    position?: GroupedPosition
    isEditingExistingSL?: boolean
    isEditingExistingTarget?: boolean
    onValidationChange?: (isValid: boolean) => void,
    showSLTargetInfo?: boolean
}> = ({ disabled, disabledCheckbox, disableStopLoss, disableTarget, initialStopLoss, initialTarget, basketPosition, strategyMode, position, isEditingExistingSL, isEditingExistingTarget, onValidationChange, showSLTargetInfo = false, modifyType }) => {
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { addStopLoss, addTarget, slValue, targetValue } = useSelector(selectExitStrategy);
    const orderHistory = useSelector(selectOrdersHistory)
    const tradeOrders = useSelector(selectTradeOrders)
    const snapshots = useSelector(selectSnapshots)
    const stopLossInputRef = useRef<HTMLInputElement>(null)
    const targetInputRef = useRef<HTMLInputElement>(null)
    const initializedRef = useRef(false);
    const effectivePosition = position || basketPosition;
    const isSingleSecurity = useMemo(() => {
        return (effectivePosition?.children?.length ?? 0 ) === 1 && effectivePosition.groupId.startsWith('default_')
    }, [effectivePosition?.children])
    const mode = useSelector(selectStrategyBuilderMode)

    const [selectedSecurity, setSelectedSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const positionKey = effectivePosition ? getSecurityKey(effectivePosition?.security?.exchange, effectivePosition?.security?.code) : ''
    useEffect(() => {
        if (!positionKey) { setSelectedSecurity(undefined); return }
        if (isSingleSecurity) {
            getSecurityAsync(positionKey).then(setSelectedSecurity)
        } else {
            getSecurityAsync(positionKey).then((posSec) => {
                const underlyingCode = posSec?.underlyingSecurityCode
                if (underlyingCode) getSecurityAsync(getSecurityKey(effectivePosition!.security!.exchange, underlyingCode)).then(setSelectedSecurity)
                else setSelectedSecurity(posSec)
            })
        }
    }, [isSingleSecurity, positionKey, effectivePosition])

    // State for points/percentage toggle
    // For editing existing orders in single securities, we use 'exact' mode
    const [slValueType, setSlValueType] = useState<SlTargetType>(isEditingExistingSL && isSingleSecurity ? 'exact' : 'points')
    const [targetValueType, setTargetValueType] = useState<SlTargetType>(isEditingExistingTarget && isSingleSecurity ? 'exact' : 'points')

    // Determine if position is long or short
    const isLongPosition = useMemo(() => {
        if (!effectivePosition) return true

        const effectiveQuantity = effectivePosition.quantity !== 0
            ? effectivePosition.quantity
            : (() => {
                const children = effectivePosition.children || []
                const totalPremium = children.reduce((sum, child) => sum + (child.avgPrice * child.quantity), 0)
                return totalPremium > 0 ? Math.abs(totalPremium) : -Math.abs(totalPremium)
            })()

        return effectiveQuantity > 0
    }, [effectivePosition])

    const incomingPositions = useMemo(() => {
        if (!effectivePosition) return []
        const analyzePosition = effectivePosition as AnalyzeGroupedPosition
        return (analyzePosition.children || []).filter(childPosition => childPosition.new || childPosition.addedQuantity)
    }, [effectivePosition])

    const netProfit = useMemo(() => {
        if (!effectivePosition) return 0
        const analyzePosition = effectivePosition as AnalyzeGroupedPosition
        let totalNetProfit = (analyzePosition.children || []).reduce((sum, childPosition) => {
            let pnl = childPosition.realizedProfit || 0
            const originalPosition = childPosition.originalPosition
            if (!originalPosition) return sum + pnl
            const direction = originalPosition.closePosition > 0 ? 1 : -1
            const originalAvgPrice = originalPosition.closePositionValue / originalPosition.closePosition
            const totalQty = Math.abs(originalPosition.closePosition)
            const squaredQty = Math.abs(childPosition.squaredOffQuantity || 0)
            const securityKey = getSecurityKey(childPosition.security.exchange, childPosition.security.code)
            const snapshot = snapshots[securityKey]
            if (!snapshot) return sum + pnl
            let unrealizedQty = totalQty - squaredQty
            let valuationPrice = snapshot.lastTradedPrice
            if (childPosition.squareOffOrderId && squaredQty > 0) {
                const squareOffOrder = tradeOrders?.find(t => t.clientOrderId === childPosition.squareOffOrderId)
                if (squareOffOrder?.selected) {
                    const squareOffPrice = squareOffOrder.price || snapshot.lastTradedPrice
                    pnl += squaredQty * (squareOffPrice - originalAvgPrice) * direction
                }
            }
            if (unrealizedQty > 0) {
                pnl += unrealizedQty * (valuationPrice - originalAvgPrice) * direction
            }
            return sum + pnl
        }, 0)

        if(mode == 'analyze' || mode == "squareOff"){
            const incomingPNL = incomingPositions.reduce((sum, position) => {
                const addedQty = position.addedQuantity ?? 0;
                if (addedQty === 0) return sum;
                const securityKey = getSecurityKey(
                    position.security.exchange,
                    position.security.code
                );
                const ltp = snapshots[securityKey]?.lastTradedPrice;
                if (!Number.isFinite(ltp)) return sum;

                // For incoming positions, use LTP as entry price so PNL = 0
                // This is visual only - doesn't affect order placement
                const orderPrice = ltp;

                const direction = Math.sign(addedQty);
                const pnl = Math.abs(addedQty) * (ltp - orderPrice) * direction;
                return sum + pnl;
            }, 0);
            return (totalNetProfit + incomingPNL) / (selectedSecurity?.priceMultiplier ?? 100)
        }

        return totalNetProfit / (selectedSecurity?.priceMultiplier ?? 100)
    }, [effectivePosition, snapshots, tradeOrders, selectedSecurity, mode, incomingPositions])

    const basketOpenSlOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
                [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.STOP_LOSS
        })
    }, [effectivePosition?.groupId, orderHistory])

    const basketInactiveSlOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
                o.status === OrderStatus.INACTIVE && o.orderType === OrderType.STOP_LOSS
        })
    }, [effectivePosition?.groupId, orderHistory]);
    const basketOpenTargetOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
                [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.TARGET
        })
    }, [effectivePosition?.groupId, orderHistory])

    const basketInactiveTargetOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
            o.status === OrderStatus.INACTIVE && o.orderType === OrderType.TARGET
        })
    }, [effectivePosition?.groupId, orderHistory])


    // Calculate the MTM that would trigger SL based on current input
    const calculatedSlValue = useMemo(() => {
        if (!addStopLoss || !slValue) return null
        
        if (isSingleSecurity) {
            if(!effectivePosition?.avgPrice) return null 
            const entryPrice = effectivePosition.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)
            if (slValueType === 'exact') {
                return isLongPosition ? (entryPrice - slValue) : (slValue - entryPrice)
            } else if (slValueType === 'points') {
                return slValue * (selectedSecurity.lotSize ?? 1)
            } else { // percentage
                const percentageChange = (entryPrice * slValue) / 100
                return  percentageChange
            }
        } else {
            // Basket mode: calculate LTP from MTM
            return slValue
        }
    }, [slValue, addStopLoss, effectivePosition, selectedSecurity, isSingleSecurity, slValueType, isLongPosition])

    // Calculate the MTM that would trigger Target based on current input
    const calculatedTargetValue = useMemo(() => {
        if (!addTarget || !targetValue) return null
        
        if (isSingleSecurity) {
            if(!effectivePosition?.avgPrice) return null
            const entryPrice = effectivePosition.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)
            if (targetValueType === 'exact') {
                return isLongPosition ? (targetValue - entryPrice) : (entryPrice - targetValue)
            } else if (targetValueType === 'points') {
                return targetValue * (selectedSecurity.lotSize ?? 1)
            } else { // percentage
                const percentageChange = (entryPrice * targetValue) / 100
                return percentageChange  
            }
        } else {
            // Basket mode: calculate LTP from MTM
            return targetValue
        }
    }, [targetValue, addTarget, effectivePosition, selectedSecurity, isSingleSecurity, targetValueType, isLongPosition])

    // Validate if Target would trigger immediately
    const isTargetValid = useMemo(() => {
        // If no target value entered, validation passes
        if (!addTarget || !targetValue) return true
        // When netProfit is undefined/null, skip validation
        if (netProfit === undefined || netProfit === null) return true

        // When netProfit is 0 (new basket), target must be positive
        if (netProfit === 0) {
            return calculatedTargetValue > 0
        }

        // For existing positions, target must be >= current profit
        return calculatedTargetValue >= netProfit
    }, [netProfit, addTarget, targetValue, calculatedTargetValue])

    // Validate if SL would trigger immediately
    const isSlValid = useMemo(() => {
        if (!addStopLoss || !slValue) return true
        // When netProfit is undefined/null, skip validation
        if (netProfit === undefined || netProfit === null) return true

        // When netProfit is 0 (new basket), SL must be positive (loss limit)
        if (netProfit === 0) {
            return calculatedSlValue > 0
        }

        // For existing positions, SL must be >= -current profit
        return calculatedSlValue >= -netProfit
    }, [calculatedSlValue, addStopLoss, slValue, netProfit])

    const targetHelperText = useMemo(() => {
        if(!isSingleSecurity || !showSLTargetInfo || !targetValue || targetValueType === 'exact' || !isTargetValid) return null
        const entryPrice = effectivePosition?.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)
        const targetPrice = isLongPosition ? entryPrice + calculatedTargetValue/(selectedSecurity?.lotSize ?? 1) : entryPrice - calculatedTargetValue/(selectedSecurity.lotSize ?? 1)
        return `Target LTP : ${targetPrice.toFixed(2)}`
    }, [isTargetValid, , calculatedTargetValue, effectivePosition, selectedSecurity, targetValueType])

    const slHelperText = useMemo(() => {
        if(!isSingleSecurity || !showSLTargetInfo || !slValue || slValueType === 'exact' || !isSlValid) return null
        const entryPrice = effectivePosition?.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)
        const slPrice = isLongPosition ? entryPrice - (calculatedSlValue/(selectedSecurity?.lotSize ?? 1)) : entryPrice + calculatedSlValue/(selectedSecurity.lotSize ?? 1)
        return `SL LTP : ${slPrice.toFixed(2)}`
    }, [isSlValid, calculatedSlValue, effectivePosition, selectedSecurity, slValueType])

    const targetInfoText = useMemo(()=>{
        if(isSingleSecurity){
            const entryPrice = effectivePosition?.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)
            return isLongPosition ? `Target must be >= ${(entryPrice + netProfit/(selectedSecurity?.lotSize ?? 1)).toFixed(2)}` : `Target must be <= ${(entryPrice + netProfit).toFixed(2)}`
        }
        return `Target must be >= ${netProfit}`
    },[netProfit, isLongPosition, isSingleSecurity])

    const slInfoText = useMemo(()=>{
        if(isSingleSecurity){
            const entryPrice = effectivePosition?.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)
            return isLongPosition ? `SL LTP must be <=  ${(entryPrice + netProfit/(selectedSecurity?.lotSize ?? 1)).toFixed(2)}` : `SL LTP must be >=  ${(entryPrice + netProfit).toFixed(2)}`
        }
        return `SL must be >= ${-netProfit}`
    },[netProfit, isLongPosition, isSingleSecurity])

    // Overall validation: check if both SL and Target are valid
    const isValid = useMemo(() => {
        // If SL is enabled and invalid, return false
        if (addStopLoss && !isSlValid) return false

        // If Target is enabled and invalid, return false
        if (addTarget && !isTargetValid) return false

        // Otherwise, validation passes
        return true
    }, [addStopLoss, isSlValid, addTarget, isTargetValid])

    // Notify parent component when validation changes
    useEffect(() => {
        onValidationChange?.(isValid)
    }, [isValid, onValidationChange])

    useEffect(() => {
        if (initializedRef.current) return;

        if (initialStopLoss !== undefined) {
            dispatch(updateExitStrategy({
                addStopLoss: true,
                slValue: initialStopLoss
            }))
        }

        if (initialTarget !== undefined) {
            dispatch(updateExitStrategy({
                addTarget: true,
                targetValue: initialTarget
            }))
        }

        initializedRef.current = true;
    }, [initialStopLoss, initialTarget]);



    useEffect(() => {
        if (!initializedRef.current) return;   // ⬅️ IMPORTANT
        if (initialStopLoss !== undefined) return;

        if (basketOpenSlOrder) {
            dispatch(updateExitStrategy({
                addStopLoss: true,
                slValue: (basketOpenSlOrder.triggerPrice ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else if (basketInactiveSlOrder) {
            dispatch(updateExitStrategy({
                addStopLoss: true,
                slValue: (basketInactiveSlOrder.triggerPrice ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else {
            dispatch(updateExitStrategy({
                addStopLoss: false,
                slValue: ''
            }))
        }
    }, [basketOpenSlOrder, basketInactiveSlOrder, selectedSecurity]);

    useEffect(() => {
        if (!initializedRef.current) return;
        if (initialTarget !== undefined) return

        if (basketOpenTargetOrder) {
            dispatch(updateExitStrategy({
                addTarget: true,
                targetValue: (basketOpenTargetOrder.price ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else if (basketInactiveTargetOrder) {
            dispatch(updateExitStrategy({
                addTarget: true,
                targetValue: (basketInactiveTargetOrder.price ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else {
            dispatch(updateExitStrategy({
                addTarget: false,
                targetValue: ''
            }))
        }
    }, [
        basketOpenTargetOrder,
        basketInactiveTargetOrder,
        selectedSecurity,
        initialTarget
    ])

    const toggleAddStopLoss = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateExitStrategy({
            addStopLoss: event.target.checked
        }))
        setTimeout(() => {
            stopLossInputRef.current?.focus()
        }, 100)
    }, []);

    const onStopLossChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value === '' ? '' : Number(event.target.value)

        dispatch(updateExitStrategy({
            slValue: inputValue
        }))
    }, [dispatch])

    const onSlValueTypeChange = useCallback((_event: React.MouseEvent<HTMLElement>, newValueType: SlTargetType) => {
        if (newValueType !== null) {
            setSlValueType(newValueType)
        }
    }, [])

    const toggleAddTarget = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateExitStrategy({
            addTarget: event.target.checked
        }))
        setTimeout(() => {
            targetInputRef.current?.focus()
        }, 100)
    }, []);

    const onTargetChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value === '' ? '' : Number(event.target.value)

        dispatch(updateExitStrategy({
            targetValue: inputValue
        }))
    }, [dispatch])

    const onTargetValueTypeChange = useCallback((_event: React.MouseEvent<HTMLElement>, newValueType: SlTargetType) => {
        if (newValueType !== null) {
            setTargetValueType(newValueType)
        }
    }, [])

    const [targetInputFocused, setTargetInputFocused] = useState(false)
    const [slInputFocused, setSlInputFocused] = useState(false)
    const [targetHovered, setTargetHovered] = useState(false)
    const [slHovered, setSlHovered] = useState(false)

    const targetField = useMemo(() => {
        const showTargetTooltip = !isTargetValid && (targetInputFocused || targetHovered)
        
        return <>
            {!disabledCheckbox && <Stack 
                direction="row" 
                alignItems="center" 
                sx={{ marginBottom: 0.5, justifyContent: 'space-between' }}
                onMouseEnter={() => setTargetHovered(true)}
                onMouseLeave={() => setTargetHovered(false)}
            >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Checkbox checked={addTarget} onChange={toggleAddTarget} size='small' sx={{ padding: 0, marginRight: 0.5 }} disabled={disabled} />
                    <Typography variant='body1' className={classes.slTargetLabel}>TARGET</Typography>
                </Box>
            </Stack>}
            <Box 
                sx={{ position: 'relative' }}
                onMouseEnter={() => setTargetHovered(true)}
                onMouseLeave={() => setTargetHovered(false)}
            >
                {showTargetTooltip && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.75rem',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            marginBottom: '4px',
                        }}
                    >
                        {targetInfoText}
                    </Box>
                )}
                <TextField
                    InputLabelProps={{
                        shrink: true
                    }}
                    type="number"
                    size="small"
                    fullWidth
                    value={targetValue}
                    disabled={!addTarget || disabled}
                    onChange={onTargetChange}
                    autoComplete='off'
                    inputRef={targetInputRef}
                    error={!isTargetValid && addTarget}
                    onFocus={() => setTargetInputFocused(true)}
                    onBlur={() => setTargetInputFocused(false)}
                    inputProps={{
                        step: 1
                    }}
                    InputProps={{
                        sx: {

                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: theme => theme.palette.text.disabled,
                            },

                            '& input[type=number]': {
                                MozAppearance: 'textfield'
                            },
                            paddingRight: 0.5,
                        },
                        endAdornment: (
                            <>
                            {!addTarget && !disableTarget && <div className={classes.disabledInputClickBackdrop} onClick={(e) => {
                                const newEvent = e as any
                                newEvent.target = { checked: true }
                                toggleAddTarget(newEvent)
                            }} />}
                            <ToggleButtonGroup
                                size="small"
                                value={isSingleSecurity ? targetValueType : 'mtm'}
                                aria-label="text alignment"
                                sx={{
                                    height: "28px",
                                    '.MuiButtonBase-root': {
                                        borderColor: (theme) => theme.palette.background.default
                                    },
                                }}
                            >
                                <ExtraSmallButton
                                    sx={{
                                        minWidth: 28,
                                        height: '28px !important',
                                        p: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => {
                                        dispatch(updateExitStrategy({
                                            addTarget: false,
                                            targetValue: ''
                                        }))
                                    }}
                                >
                                    <ClearIcon sx={{ fontSize: 14 }} />
                                </ExtraSmallButton>
                                {isSingleSecurity ? (
                                    targetValueType !== 'exact' && (
                                        <Box component="span" sx={{ display: 'contents' }}>
                                            <ToggleButton
                                                value="points"
                                                onClick={onTargetValueTypeChange}
                                                sx={{ color: (theme) => targetValueType === 'points' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                Pts
                                            </ToggleButton>
                                            <ToggleButton
                                                value="percentage"
                                                onClick={onTargetValueTypeChange}
                                                sx={{ color: (theme) => targetValueType === 'percentage' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                %
                                            </ToggleButton>
                                        </Box>
                                    )
                                ) : (
                                    <ToggleButton aria-label="left aligned" selected value="mtm">
                                        MTM
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </>
                    )
                }}
            />
            </Box>
            <FormHelperText sx={{ minHeight: '20px' }}>
                {targetHelperText}
            </FormHelperText>
        </>
    }, [targetInfoText, selectedSecurity, addTarget, targetValue, toggleAddTarget, onTargetChange, disabled, isSingleSecurity, targetValueType, onTargetValueTypeChange, disableTarget, isTargetValid, targetInputFocused, targetHovered])


    const stopLossField = useMemo(() => {
        const showSlTooltip = !isSlValid && (slInputFocused || slHovered)
        return <>
            {!disabledCheckbox && <Stack 
                direction="row" 
                alignItems="center" 
                sx={{ marginBottom: 0.5, justifyContent: 'space-between' }}
                onMouseEnter={() => setSlHovered(true)}
                onMouseLeave={() => setSlHovered(false)}
            >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Checkbox checked={addStopLoss} onChange={toggleAddStopLoss} size='small' sx={{ padding: 0, marginRight: 0.5 }} disabled={disabled} />
                    <Typography variant='body1' className={classes.slTargetLabel}>STOP LOSS</Typography>
                </Box>
            </Stack>}
            <Box 
                sx={{ position: 'relative' }}
                onMouseEnter={() => setSlHovered(true)}
                onMouseLeave={() => setSlHovered(false)}
            >
                {showSlTooltip && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.75rem',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            marginBottom: '4px',
                        }}
                    >
                        {slInfoText}
                    </Box>
                )}
                <TextField
                    InputLabelProps={{
                        shrink: true
                    }}
                    type="number"
                    size="small"
                    fullWidth
                    value={slValue}
                    disabled={!addStopLoss || disabled}
                    onChange={onStopLossChange}
                    autoComplete='off'
                    inputRef={stopLossInputRef}
                    error={!isSlValid && addStopLoss}
                    onFocus={() => setSlInputFocused(true)}
                    onBlur={() => setSlInputFocused(false)}
                    inputProps={{
                        step: 1,
                    }}
                    InputProps={{
                        sx: {
                            '& input[type=number]': {
                                MozAppearance: 'textfield'
                            },
                            paddingRight: 0.5,
                        },
                        endAdornment: (
                            <>
                            {!addStopLoss && !disableStopLoss && <div className={classes.disabledInputClickBackdrop} onClick={(e) => {
                                const newEvent = e as any
                                newEvent.target = { checked: true }
                                toggleAddStopLoss(newEvent)
                            }} />}
                            <ToggleButtonGroup
                                size="small"
                                value={isSingleSecurity ? slValueType : 'mtm'}
                                aria-label="text alignment"
                                sx={{
                                    height: "28px",
                                    '.MuiButtonBase-root': {
                                        borderColor: (theme) => theme.palette.background.default
                                    },
                                }}
                            >
                                <ExtraSmallButton
                                    sx={{
                                        minWidth: 28,
                                        height: '28px !important',
                                        p: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => {
                                        dispatch(updateExitStrategy({
                                            addStopLoss: false,
                                            slValue: ''
                                        }))
                                    }}
                                >
                                    <ClearIcon sx={{ fontSize: 14 }} />
                                </ExtraSmallButton>
                                {isSingleSecurity ? (
                                    slValueType !== 'exact' && (
                                        <Box component="span" sx={{ display: 'contents' }}>
                                            <ToggleButton
                                                value="points"
                                                onClick={onSlValueTypeChange}
                                                sx={{ color: (theme) => slValueType === 'points' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                Pts
                                            </ToggleButton>
                                            <ToggleButton
                                                value="percentage"
                                                onClick={onSlValueTypeChange}
                                                sx={{ color: (theme) => slValueType === 'percentage' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                %
                                            </ToggleButton>
                                        </Box>
                                    )
                                ) : (
                                    <ToggleButton aria-label="left aligned" selected value="mtm">
                                        MTM
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </>
                    )
                }}
            />
            </Box>
            <FormHelperText sx={{ minHeight: '20px' }}>
                {slHelperText}
            </FormHelperText>
        </>
    }, [slInfoText, selectedSecurity, addStopLoss, slValue, toggleAddStopLoss, onStopLossChange, disabled, isSingleSecurity, slValueType, onSlValueTypeChange, disableStopLoss, isSlValid, slInputFocused, slHovered])

    return (
        <Grid container gap={2} className={disabled ? classes.disabled : undefined} sx={{ paddingTop: 1 }} >
            <Grid container item xs={12} spacing={2}>
                {(!modifyType || modifyType === ModifyType.STOPLOSS) && <Grid item xs={6} paddingLeft={0} >
                    {stopLossField}
                </Grid>}
                {(!modifyType || modifyType === ModifyType.TARGET) && <Grid item xs={6} paddingLeft={0} >
                    {targetField}
                </Grid>}
            </Grid>
        </Grid>
    )
}

export default memo(SlAndTargetInputs)